import React, { useState, useMemo } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentsApi } from '../../_lib/api-client';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useSettings } from '../../_providers/SettingsProvider';

interface UpgradeCardProps {
  title?: string;
  subtitle?: string;
  showCurrentStatus?: boolean;
  currentGuestCount?: number;
  maxFreeGuests?: number;
  currentEvent?: any;
  eventId?: string;
  compact?: boolean;
  className?: string;
}

const CheckoutForm: React.FC<{ successUrl: string; onCancel: () => void }> = ({ successUrl, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: successUrl },
    });

    if (confirmError) {
      setError(confirmError.message || 'Payment failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isSubmitting}
          className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-medium py-3 px-6 rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Processing...' : 'Pay Now'}
        </button>
      </div>
    </form>
  );
};

const UpgradeCard: React.FC<UpgradeCardProps> = ({
  title = "Upgrade to Paid Event",
  subtitle = "Remove all limits and unlock premium features for your wedding",
  showCurrentStatus = false,
  currentGuestCount = 15,
  maxFreeGuests = 15,
  currentEvent,
  eventId,
  compact = false,
  className = "",
}) => {
  useKindeAuth();
  const siteConfig = useSettings();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [priceData, setPriceData] = useState<{
    amount: number;
    currency: string;
    description: string;
    formatted: string;
  } | null>(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(true);

  const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
    if (!siteConfig.stripePublishableKey) return null;
    return loadStripe(siteConfig.stripePublishableKey);
  }, [siteConfig.stripePublishableKey]);

  // Fetch live price from GraphQL API
  const fetchLivePrice = async () => {
    try {
      const productCode = 'PAID_EVENT';

      const priceDetails = await paymentsApi.getPriceDetails(productCode);

      const amountInDollars = priceDetails.amount / 100;
      const hasCents = priceDetails.amount % 100 !== 0;

      const formatted = new Intl.NumberFormat('en-NZ', {
        style: 'currency',
        currency: priceDetails.currency,
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2
      }).format(amountInDollars);

      // Remove country code from currency (e.g., "US$" -> "$", "NZ$" -> "$")
      const simplifiedFormatted = formatted.replace(/^[A-Z]{2}\$/, '$');

      setPriceData({
        amount: priceDetails.amount,
        currency: priceDetails.currency,
        description: priceDetails.description,
        formatted: simplifiedFormatted
      });
    } catch (error) {
      console.error('Failed to fetch live price:', error);
      setError('Failed to load pricing information. Please refresh the page.');
    } finally {
      setIsLoadingPrice(false);
    }
  };

  React.useEffect(() => {
    fetchLivePrice();
  }, []);

  const eventIdToUse = currentEvent?.id || eventId;

  const handleCheckout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!stripePromise) {
        throw new Error('Payment system not ready. Please try again.');
      }

      if (!eventIdToUse) {
        throw new Error('Event information not available. Please refresh the page and try again.');
      }

      const { clientSecret: secret } = await paymentsApi.createPaymentIntent(eventIdToUse, 'PAID_EVENT');
      setClientSecret(secret);
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout process');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { text: 'Unlimited invited guests (was 15)', included: true },
  ];

  if (isLoadingPrice || siteConfig.isLoading) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl overflow-hidden ${className}`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing information...</p>
        </div>
      </div>
    );
  }

  if (siteConfig.error) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl overflow-hidden ${className}`}>
        <div className="p-8 text-center">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600">Failed to load configuration. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  const displayPrice = priceData?.formatted || 'NZ$51';

  if (clientSecret && stripePromise) {
    const successUrl = `${window.location.origin}/app/billing/success?eventId=${eventIdToUse}`;
    return (
      <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden ${className}`}>
        <div className="p-8">
          <h2 className="font-medium text-gray-900 mb-6 text-xl">Complete your payment</h2>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm successUrl={successUrl} onCancel={() => setClientSecret(null)} />
          </Elements>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden ${className}`}>
      {/* Price Header */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 text-center">
        <h2 className={`font-medium text-gray-200 mb-4 ${compact ? 'text-lg' : 'text-xl'}`}>{title}</h2>
        <div className="flex items-baseline justify-center mb-2">
          <span className={`font-semibold text-white ${compact ? 'text-4xl' : 'text-5xl'}`}>
            {displayPrice}
          </span>
          <span className={`ml-2 text-gray-400 ${compact ? 'text-base' : 'text-lg'}`}>one-time</span>
        </div>
        <p className="text-sm text-gray-400">
          {priceData?.description || subtitle}
        </p>
      </div>

      {/* Content */}
      <div className="p-8">
        {/* Current Status Alert (if shown) */}
        {showCurrentStatus && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center">
              <svg className="h-5 w-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h3 className="text-sm font-medium text-amber-900">
                Guest limit reached
              </h3>
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mb-8 bg-gray-50 rounded-xl p-6">
          <div className="flex items-center justify-center">
            <div className="bg-green-100 rounded-full p-2 mr-3">
              <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-gray-900 font-medium">Unlimited invited guests</span>
            <span className="text-gray-500 ml-2">(currently limited to 15)</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Checkout Button */}
        <button
          onClick={handleCheckout}
          disabled={isLoading || !stripePromise || !eventIdToUse}
          className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white font-medium py-3 px-6 rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : !eventIdToUse ? (
            'Loading event information...'
          ) : (
            `Upgrade Now - ${displayPrice}`
          )}
        </button>

        {/* Trust Badges */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-center space-x-6 text-xs text-gray-400">
            <span className="flex items-center">
              <svg className="h-3.5 w-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Secure checkout
            </span>
            <span className="flex items-center">
              <svg className="h-3.5 w-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
              </svg>
              Powered by Stripe
            </span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UpgradeCard;
